/**
 * Return a date string for the given date
 * @param {*} date - A string or unix stamp number representation of the date
 * @param {String} locale - User locale, ex: 'en-US'
 * @return {String} - Date string, ex: 'Apr 25, 2015'
 */
export function toDateString(date, locale) {
  const dateOpts = { month: 'short', year: 'numeric', day: 'numeric' };
  return new Date(date).toLocaleDateString(locale, dateOpts);
}

/**
 * Return a date string for the given iso-format date
 * @param {String} isoDate - ISO-format date, ex: '2015-04-25'
 * @param {String} locale - User locale, ex: 'en-US'
 * @return {String} - Date string, ex: 'Apr 25, 2015'
 */
export function isoToDateString(isoDate, locale) {
  if (isoDate) {
    // Convert date to non-ISO format. Otherwise, Date assumes the local time zone and messes
    // up the date (ex: converts April 25 to April 24 at 9pm)
    const [year, month, day] = isoDate.split('-');
    const dateNonIso = [month, day, year].join('/');

    return toDateString(dateNonIso, locale);
  }

  return isoDate;
}

/**
 * Returns an array of all the days of a given year.
 * @param {String} year
 * @return {Array} - the days
 */
export function getDaysOfYear(year) {
  const days = Array.from({ length: 366 }, (_, i) => {
    const date = new Date(`${year}-01-01`);
    date.setDate(date.getDate() + i);
    return date.toISOString().split('T')[0];
  });
  // Leap years
  if (Number(days[365].split('-')[0]) !== Number(year)) {
    days.pop();
  }
  return days;
}

export function getAmountOfDaysBetween(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return Math.floor((end - start) / (1000 * 60 * 60 * 24));
}

/**
 * Returns an array of all the days of a date range.
 * @param {String} year
 * @return {Array} - the days
 */
export function getDaysOfRange(startDate, endDate) {
  const diffDays = getAmountOfDaysBetween(startDate, endDate);
  const days = Array.from({ length: Math.abs(diffDays) + 1  }, (_, i) => {
    const date = new Date(startDate);
    const shouldSumDays = diffDays > 0;
    const newDateIncrement = shouldSumDays ? date.getDate() + i : date.getDate() - i;
    date.setDate(newDateIncrement);
    return date.toISOString().split('T')[0];
  });

  return days;
}

/**
 * Sort an array of dates in descending order.
 * @param {Array} dates the dates
 * @return {Array} the sorted array
 */
export function sortDatesDescending(dates) {
  return dates.sort((a, b) => {
    if (new Date(a) > new Date(b)) {
      return -1;
    }
    if (new Date(b) < new Date(a)) {
      return 1;
    }
    return 0;
  });
}
